import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatBRL, formatPct } from "@/lib/format";
import { defaultPeriod } from "@/lib/periods";
import { PeriodFilter } from "@/components/PeriodFilter";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Cell,
} from "recharts";
import { Loader2, TrendingUp, Wallet, Layers, ArrowRight, AlertTriangle, Info } from "lucide-react";

const Stat = ({ label, value, sub, tone, testid }) => {
  const t = { accent: "text-accent", success: "text-emerald-600", danger: "text-red-600", warning: "text-amber-600" }[tone] || "text-foreground";
  return (
    <div data-testid={testid}>
      <p className="section-label">{label}</p>
      <p className={`font-num text-2xl font-bold mt-1 ${t}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
};

const alertIcon = { danger: AlertTriangle, warning: AlertTriangle, info: Info };
const alertTone = { danger: "border-red-200 bg-red-50 text-red-700", warning: "border-amber-200 bg-amber-50 text-amber-800", info: "border-sky-200 bg-sky-50 text-sky-800" };

export default function Financeiro() {
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState(defaultPeriod());
  const [data, setData] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, m] = await Promise.all([
        api.get(`/finance/summary`, { params: { date_from: period.from, date_to: period.to } }),
        api.get(`/finance/monthly`, { params: { months: 12 } }),
      ]);
      setData(s.data);
      setMonthly(m.data);
    } finally { setLoading(false); }
  }, [period.from, period.to]);

  useEffect(() => { load(); }, [load, currentOrg?.id]);

  const c = data?.current;
  const be = c?.ponto_equilibrio;
  const distance = c && be != null ? c.receita_liquida - be : null;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">A Tríade — Custos × Margem × Faturamento</h1>
          <p className="text-muted-foreground mt-1">Gestão financeira gerencial baseada em dados realizados</p>
        </div>
        <PeriodFilter value={period} onChange={setPeriod} />
      </div>

      {loading || !c ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
      ) : (
        <>
          {data.alerts.length > 0 && (
            <div className="space-y-2 mb-6" data-testid="finance-alerts">
              {data.alerts.map((a) => {
                const Icon = alertIcon[a.level] || Info;
                return (
                  <div key={`${a.level}-${a.text}`} className={`flex items-start gap-2 rounded-lg border px-4 py-2.5 text-sm ${alertTone[a.level] || alertTone.info}`}>
                    <Icon className="h-4 w-4 mt-0.5 shrink-0" /> <span>{a.text}</span>
                  </div>
                );
              })}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
            <div className="rounded-xl border border-border bg-card p-5 shadow-sm" data-testid="triade-faturamento">
              <div className="flex items-center gap-2 mb-4"><Wallet className="h-4 w-4 text-accent" /><h3 className="font-display font-semibold">Faturamento</h3></div>
              <div className="space-y-4">
                <Stat label="Receita Bruta" value={formatBRL(c.receita_bruta)} />
                <Stat label="Receita Líquida" value={formatBRL(c.receita_liquida)} tone="accent" sub={`(-) Deduções ${formatBRL(c.deducoes)}`} />
              </div>
            </div>
            <div className="rounded-xl border border-border bg-card p-5 shadow-sm" data-testid="triade-margem">
              <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-4 w-4 text-emerald-600" /><h3 className="font-display font-semibold">Margem</h3></div>
              <div className="space-y-4">
                <Stat label="Margem Bruta" value={formatPct(c.margem_bruta_pct)} sub={formatBRL(c.margem_bruta)} tone="success" />
                <Stat label="Margem de Contribuição" value={formatPct(c.imc)} sub={`${formatBRL(c.margem_contribuicao)} · IMC`} tone="success" />
              </div>
            </div>
            <div className="rounded-xl border border-border bg-card p-5 shadow-sm" data-testid="triade-custos">
              <div className="flex items-center gap-2 mb-4"><Layers className="h-4 w-4 text-amber-600" /><h3 className="font-display font-semibold">Custos</h3></div>
              <div className="space-y-4">
                <Stat label="CMV" value={formatBRL(c.cmv)} />
                <div className="grid grid-cols-2 gap-3">
                  <Stat label="Desp. Fixas" value={formatBRL(c.despesas_fixas)} tone="warning" />
                  <Stat label="Desp. Variáveis" value={formatBRL(c.despesas_variaveis)} tone="warning" />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="rounded-xl border border-border bg-card p-4"><Stat label="Resultado Operacional" value={formatBRL(c.resultado_operacional)} tone={c.resultado_operacional >= 0 ? "success" : "danger"} testid="kpi-resultado" /></div>
            <div className="rounded-xl border border-border bg-card p-4"><Stat label="Ponto de Equilíbrio" value={be == null ? "N/A" : formatBRL(be)} testid="kpi-breakeven" /></div>
            <div className="rounded-xl border border-border bg-card p-4"><Stat label="Distância do Equilíbrio" value={distance == null ? "N/A" : formatBRL(Math.abs(distance))} sub={distance == null ? "" : distance >= 0 ? "acima do equilíbrio" : "abaixo do equilíbrio"} tone={distance == null ? "default" : distance >= 0 ? "success" : "danger"} testid="kpi-distance" /></div>
            <div className="rounded-xl border border-border bg-card p-4"><Stat label="Margem de Segurança" value={c.margem_seguranca == null ? "N/A" : formatBRL(c.margem_seguranca)} sub={c.margem_seguranca_pct == null ? "" : formatPct(c.margem_seguranca_pct)} testid="kpi-mos" /></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
              <h3 className="font-display font-semibold mb-4">Ponto de Equilíbrio × Receita Líquida</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={[{ name: "Ponto de Equilíbrio", v: be || 0 }, { name: "Receita Líquida", v: c.receita_liquida }]}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" fontSize={12} /><YAxis fontSize={11} width={70} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => formatBRL(v)} />
                  <Bar dataKey="v" radius={[6, 6, 0, 0]}>
                    <Cell fill="#94a3b8" /><Cell fill={c.receita_liquida >= (be || 0) ? "#059669" : "#dc2626"} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
              <h3 className="font-display font-semibold mb-4">Visão mensal (12 meses)</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" fontSize={10} tickFormatter={(m) => m.slice(5)} /><YAxis fontSize={11} width={70} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => formatBRL(v)} />
                  <Line type="monotone" dataKey="receita_liquida" name="Receita Líquida" stroke="#0284C7" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="ponto_equilibrio" name="Ponto Equilíbrio" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                  <Line type="monotone" dataKey="resultado_operacional" name="Resultado" stroke="#059669" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card shadow-sm mb-6" data-testid="finance-comparison">
            <div className="px-5 py-4 border-b border-border"><h3 className="font-display font-semibold">Comparativo com período anterior equivalente</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-muted-foreground border-b border-border">
                  <th className="px-5 py-2 font-medium">Indicador</th><th className="px-5 py-2 text-right font-medium">Atual</th>
                  <th className="px-5 py-2 text-right font-medium">Anterior</th><th className="px-5 py-2 text-right font-medium">Variação R$</th><th className="px-5 py-2 text-right font-medium">Variação %</th>
                </tr></thead>
                <tbody>
                  {[["receita_liquida", "Receita Líquida"], ["margem_bruta", "Margem Bruta"], ["margem_contribuicao", "Margem de Contribuição"], ["despesas_variaveis", "Despesas Variáveis"], ["despesas_fixas", "Despesas Fixas"], ["resultado_operacional", "Resultado Operacional"], ["ponto_equilibrio", "Ponto de Equilíbrio"]].map(([k, label]) => {
                    const v = data.comparison[k];
                    return (
                      <tr key={k} className="border-b border-border last:border-0">
                        <td className="px-5 py-2.5">{label}</td>
                        <td className="px-5 py-2.5 text-right font-num">{v.current == null ? "N/A" : formatBRL(v.current)}</td>
                        <td className="px-5 py-2.5 text-right font-num text-muted-foreground">{v.previous == null ? "N/A" : formatBRL(v.previous)}</td>
                        <td className="px-5 py-2.5 text-right font-num">{v.abs == null ? "—" : formatBRL(v.abs)}</td>
                        <td className="px-5 py-2.5 text-right font-num">{v.pct == null ? "—" : formatPct(v.pct)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button onClick={() => navigate("/dre")} className="inline-flex items-center gap-1 text-sm text-accent font-medium hover:underline" data-testid="link-dre">DRE Gerencial <ArrowRight className="h-4 w-4" /></button>
            <button onClick={() => navigate("/despesas")} className="inline-flex items-center gap-1 text-sm text-accent font-medium hover:underline" data-testid="link-despesas">Despesas do período <ArrowRight className="h-4 w-4" /></button>
            <button onClick={() => navigate("/vendas")} className="inline-flex items-center gap-1 text-sm text-accent font-medium hover:underline" data-testid="link-vendas">Vendas <ArrowRight className="h-4 w-4" /></button>
            <button onClick={() => navigate("/metas")} className="inline-flex items-center gap-1 text-sm text-accent font-medium hover:underline" data-testid="link-metas">Metas & Simulador <ArrowRight className="h-4 w-4" /></button>
          </div>
        </>
      )}
    </div>
  );
}
